<template>
<easy-data-table
	class="data-table m-0 p-0"
	table-class-name="customize-table"
	:key="rowsPerPage"
	:headers="tableHeaders"
	:items="tableItems"
	theme-color="rgb(198, 199, 200)"
	sort-by="time"
	sort-type="desc"
	:filter-options="filterOptions"
	:rows-items="[10,20,40,100]"
	:rows-per-page="rowsPerPage"
	rows-per-page-message="записів на сторінці"
	rows-of-page-separator-message=" з "
	:table-min-height="680"
	:table-height="easyDataTableHeight()"
	:loading="isLoadingAlerts"
	alternating
	buttons-pagination
>
	<!-- If alerts list is empty -->
	<template #empty-message>
		Наразі немає сповіщень
	</template>

	<template #loading>
		<div class="spinner-border" style="width: 3rem; height: 3rem;" role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
	</template>

	<!-- Time item customization -->
	<template #item-time="{ time }">
		{{ dateTimeToString(time, false, false, true) }}
	</template>

	<!-- Type item cutomization -->
	<template #item-type="{ type, typeTitle }">
		<span
			:class="{
				'text-danger': typeTransition(type) === 'activation',
				'text-success': typeTransition(type) === 'return'
			}"
		>
			{{ typeTitle }}
		</span>
	</template>

	<!-- Value item cutomization -->
	<template #item-value="{ value }">
		<span
			:class="{
				'text-primary':['short_circuit', 'no_breakdown'].includes(value),
				'text-info': value === 'circuit_connected',
				'text-danger': ['circuit_disconnected', 'low_threshold', 'hi_threshold'].includes(value),
				'text-purple': value === 'normal_state_return',
			}"
		>
			{{ valueTitle(value) }}
		</span>
	</template>

	<!-- Nominal item customization -->
	<template #item-nominal="{ nominal }">
		{{ truncateIfFloat(nominal) }}
	</template>

	<!-- Filter to organization column -->
	<template #[orgNameHeaderSlotName]="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="organizationCriterias.length > 0"
			:criterias="organizationCriterias"
			:default-checked="sessionStore.getFilter($route.name, 'organization')"
			showSearch
			@checked-criterias="(criterias) => { saveFiters('organization', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Filter to class column -->
	<template #header-alertClass="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="classCriterias.length > 0"
			:criterias="classCriterias"
			:default-checked="sessionStore.getFilter($route.name, 'class')"
			@checked-criterias="(criterias) => { saveFiters('class', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Filter to type column -->
	<template #header-type="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="typeCriterias.length > 0"
			:criterias="typeCriterias"
			:default-checked="sessionStore.getFilter($route.name, 'type')"
			@checked-criterias="(criterias) => { saveFiters('type', criterias) }"
		/>
		{{ header.text }}
	</template>
</easy-data-table>
</template>


<script>
import { watchEffect, } from 'vue'
import TableFilterCheckboxes from '@/components/TableFilterCheckboxes.vue'
// https://hc200ok.github.io/vue3-easy-data-table-doc/props/common-props.html
import { useAccontStore } from '@/stores/AccountStore'
import { useStationsStore } from '@/stores/StationsStore'
import { useSessionStore } from '@/stores/SessionStore'
import {
	alertSchema,
	useAlerts,
} from '@/composables/alerts.js'
import { Station } from '@/models/station.js'
import {
	dateTimeToString,
	truncateIfFloat,
	easyDataTableHeight,
	easyDataTableRowsPerPage,
} from '@/helpers.js'


export default {
	components: {
		TableFilterCheckboxes,
	},

	props: {
		timeFrom: {
			type: Date,
			require: true,
		},
		
		timeTo: {
			type: Date,
			require: true,
		},

		stationId: {
			type: String,
			require: false,
			default: null, // if null - table for all stations of organization
		},

		height: {
			type: Number,
			require: false,
			default: null,
		},
	},

	setup(props) {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const sessionStore = useSessionStore()
		const {
			allAlerts,
			isLoading: isLoadingAlerts,
			setFilters: setAlertFilters,
		} = useAlerts()

		watchEffect(() => {
			setAlertFilters({
				fromTime: props.timeFrom,
				toTime: props.timeTo,
				stationIds: props.stationId === null ? null : [props.stationId, ],
			})
		})

		return {
			dateTimeToString,
			easyDataTableHeight,
			truncateIfFloat,

			accountStore,
			stationsStore,
			sessionStore,

			allAlerts,
			isLoadingAlerts,
		}
	},

	data() {
		return {
			// Table
			rowsPerPage: 20,
			// Filters
			filters: {
				organization: [],
				class: [],	// Alert class
				type: [],	// Alert type
			},
		}
	},

	created() {},

	mounted() {
		this.rowsPerPage = easyDataTableRowsPerPage()
	},

	methods: {
		saveFiters(filterName, criterias) {
			this.filters[filterName] = criterias;
			const filters = this.sessionStore.getFilter(this.$route.name, filterName);
			filters.splice(0, filters.length);
			filters.push(...criterias);
		},

		typeTransition(type) {
			return alertSchema.type
				.findType(type)
				.transition
		},

		typeTitle(type) {
			return alertSchema.type
				.findType(type)
				.title
		},

		valueTitle(value) {
			return alertSchema.value
				.findValue(value)
				.title
		},

		classTitle(classId) {
			return alertSchema.class
				.findClass(classId)
				.title
		},
	},

	computed: {
		tableHeaders() {
			const headers = []

			// If table for all stations
			if (this.stationId === null) {
				headers.push({ text: "Станція", value: Station.passportKeys.name, sortable: true, })
				if (this.accountStore.isPrimary) {
					headers.push({ text: "Організація", value: Station.passportKeys.orgName, sortable: true, })
				}
			// If table for one station
			} else {
				//
			}

			headers.push({ text: "Час", value: "time", width: 160, sortable: true, })
			headers.push({ text: "Клас", value: "alertClass"})
			headers.push({ text: "Тип", value: "type" })
			headers.push({ text: "Значення", value: "value" })
			headers.push({ text: "Номінал", value: "nominal" })
			headers.push({ text: "Лічильник", value: "counter" })

			return headers
		},

		tableItems() {
			const items = []
			
			for (const alert of this.allAlerts) {
					const alertStation = this.stationsStore.station(alert[alertSchema.stationId.key])
					let row = new Object()

					// If table for all stations
					if (this.stationId === null) {
						row[Station.passportKeys.name] = alertStation?.shortName ?? alertStation?.name
						row[Station.passportKeys.orgName] = alertStation?.passport?.get(Station.passportKeys.orgName)
					}
					row = Object.assign(row, {
						'time': alert[alertSchema.time.key],
						'alertClass': this.classTitle(alert[alertSchema.class.key]),
						'type': alert[alertSchema.type.key],
						'typeTitle': this.typeTitle(alert[alertSchema.type.key]),
						'value': alert[alertSchema.value.key],
						'nominal': alert[alertSchema.nominal.key],
						'counter': alert[alertSchema.counter.key],
					})

					items.push(row)
				}
			
			return items
		},

		organizationCriterias() {
			// Only present organizations
			return [...new Set(
				this.tableItems.map((item) => item[Station.passportKeys.orgName])
			)]
		},

		classCriterias() {
			// All classes
			return Object.values(alertSchema.class.allowed).map((el) =>  this.classTitle(el.key))
		},

		typeCriterias() {
			// All types
			return Object.values(alertSchema.type.allowed).map((el) =>  el.title)
		},

		filterOptions() {
			const filterOptionsArray = new Array()

			if (this.stationId === null) {
				filterOptionsArray.push({
					field: Station.passportKeys.orgName,
					comparison: 'in',
					criteria: this.filters.organization,
				})
			}
	
			filterOptionsArray.push({
				field: 'alertClass',
				comparison: 'in',
				criteria: this.filters.class,
			})

			filterOptionsArray.push({
				field: 'typeTitle',
				comparison: 'in',
				criteria: this.filters.type,
			})

			return filterOptionsArray;
		},

		orgNameHeaderSlotName() { return "header-" + Station.passportKeys.orgName },
	},
}
</script>


<style src="@/assets/table.css" scoped></style>
<style scoped>
	.text-purple {
		color: rgb(184, 119, 217);
	}
</style>